import React, { useState, useEffect, useCallback } from "react";
import { addBlogPost, getBlogPosts } from './services/blogService';
import { signInAdmin, signOutAdmin, getCurrentUser } from "./services/authService";
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from './firebase';

const initialState = {
  title: "",
  summary: "",
  content: "",
  category: "Design Philosophy",
  author: "Between the Walls Team",
  image: "",
  tags: "",
  readTime: "5 min read",
  status: "published"
};

export default function AddBlog() {
  const [form, setForm] = useState(initialState);
  const [activeMode, setActiveMode] = useState('edit');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const showToast = useCallback((message, type = "info") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 4000);
  }, []);

  const loadBlogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getBlogPosts(100, null, true); // Get up to 100 blogs for management, with admin access
      setBlogs(result.blogs);
    } catch (error) {
      console.error('Error loading blogs:', error);
      showToast('Error loading blogs. Please try again.', "error");
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
  }, []);

  useEffect(() => {
    if (activeMode === 'manage') {
      loadBlogs();
    }
  }, [activeMode, loadBlogs]);

  const handleEditBlog = useCallback((blog) => {
    setForm({
      ...blog,
      tags: blog.tags ? blog.tags.join(', ') : ''
    });
    setActiveMode('edit');
  }, []);

  const handleDeleteBlog = useCallback(async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        const blogRef = doc(db, 'blogs', blogId);
        await deleteDoc(blogRef);
        showToast('Blog deleted successfully!', "success");
        loadBlogs(); // Reload the blog list
      } catch (error) {
        console.error('Error deleting blog:', error);
        showToast('Error deleting blog. Please try again.', "error");
      }
    }
  }, [showToast, loadBlogs]);

  async function handleLogin(e) {
    e.preventDefault();
    setLoginError("");
    
    try {
      const user = await signInAdmin(loginEmail, loginPassword);
      setUser(user);
      setShowLogin(false);
      showToast("Logged in successfully!", "success");
    } catch (error) {
      setLoginError(error.message);
    }
  }

  async function handleLogout() {
    try {
      await signOutAdmin();
      setUser(null);
      showToast("Logged out successfully", "info");
    } catch (error) {
      showToast("Error logging out", "error");
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  function formatContentForPreview(content) {
    if (!content) return "";
    
    // Convert simple markdown-style formatting to HTML
    let formatted = content
      // Convert line breaks to paragraphs
      .split('\n\n')
      .map(paragraph => {
        if (paragraph.trim() === '') return '';
        
        // Handle headers
        if (paragraph.startsWith('## ')) {
          return `<h2>${paragraph.substring(3)}</h2>`;
        }
        if (paragraph.startsWith('### ')) {
          return `<h3>${paragraph.substring(4)}</h3>`;
        }
        
        // Handle blockquotes
        if (paragraph.startsWith('> ')) {
          return `<blockquote>${paragraph.substring(2)}</blockquote>`;
        }
        
        // Handle unordered lists
        if (paragraph.includes('\n- ') || paragraph.startsWith('- ')) {
          const listItems = paragraph
            .split('\n')
            .filter(line => line.trim().startsWith('- '))
            .map(line => `<li>${line.substring(2)}</li>`)
            .join('');
          return `<ul>${listItems}</ul>`;
        }
        
        // Handle ordered lists
        if (/^\d+\.\s/.test(paragraph) || paragraph.includes(/\n\d+\.\s/)) {
          const listItems = paragraph
            .split('\n')
            .filter(line => /^\d+\.\s/.test(line.trim()))
            .map(line => `<li>${line.replace(/^\d+\.\s/, '')}</li>`)
            .join('');
          return `<ol>${listItems}</ol>`;
        }
        
        // Handle bold and italic
        let processedParagraph = paragraph
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>')
          .replace(/__(.*?)__/g, '<strong>$1</strong>')
          .replace(/_(.*?)_/g, '<em>$1</em>');
        
        return `<p>${processedParagraph}</p>`;
      })
      .join('');
    
    return formatted;
  }

  function handleSubmit(e) {
    e.preventDefault();
    handleFirebaseSubmit();
  }

  async function handleFirebaseSubmit() {
    setIsSubmitting(true);
    
    try {
      // Validate required fields
      if (!form.title || !form.summary || !form.content) {
        showToast("Please fill in all required fields (Title, Summary, Content)", "error");
        setIsSubmitting(false);
        return;
      }

      // Prepare blog data for Firestore
      const blogToSave = {
        title: form.title,
        summary: form.summary,
        content: form.content,
        category: form.category,
        author: form.author,
        image: form.image || 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1200&q=80',
        tags: form.tags ? form.tags.split(',').map(tag => tag.trim()) : [],
        readTime: form.readTime,
        status: form.status,
        date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
      };

      // Submit to Firebase using blog service
      const result = await addBlogPost(blogToSave);
      
      showToast(`Blog "${form.title}" published successfully!`, "success");
      console.log('Blog added with ID:', result.id);
      
      // Reset form
      setForm(initialState);
      setActiveMode('edit');
      
    } catch (error) {
      console.error('Error submitting blog:', error);
      showToast('Error publishing blog. Please try again.', "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  function renderPreview() {
    return (
      <div style={{ background: "#0d0d0d", minHeight: "100vh", color: "#f7f5ec", padding: "40px 20px" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          {/* Blog Header */}
          <div style={{ marginBottom: "40px" }}>
            <div style={{ 
              color: "#B8B7AA", 
              fontSize: "0.9rem", 
              marginBottom: "12px",
              textTransform: "uppercase",
              letterSpacing: "1px"
            }}>
              [{form.category}]
            </div>
            <h1 style={{ 
              fontSize: "2.5rem", 
              fontWeight: "700", 
              marginBottom: "20px",
              lineHeight: "1.2",
              color: "#f7f5ec"
            }}>
              {form.title || "Blog Title"}
            </h1>
            <div style={{ 
              color: "#B8B7AA", 
              fontSize: "1.1rem", 
              marginBottom: "24px",
              lineHeight: "1.6"
            }}>
              {form.summary || "Blog summary will appear here..."}
            </div>
            <div style={{ 
              display: "flex", 
              gap: "20px", 
              color: "#B8B7AA", 
              fontSize: "0.9rem" 
            }}>
              <span>By {form.author}</span>
              <span>•</span>
              <span>{form.readTime}</span>
              <span>•</span>
              <span>{new Date().toLocaleDateString()}</span>
            </div>
          </div>

          {/* Featured Image */}
          {form.image && (
            <div style={{ marginBottom: "40px" }}>
              <img 
                src={form.image} 
                alt={form.title}
                style={{ 
                  width: "100%", 
                  height: "400px", 
                  objectFit: "cover", 
                  borderRadius: "12px" 
                }}
              />
            </div>
          )}

          {/* Blog Content */}
          <div style={{ 
            color: "#D6D0C3", 
            fontSize: "1.1rem", 
            lineHeight: "1.8",
            marginBottom: "40px"
          }}>
            <div 
              dangerouslySetInnerHTML={{ 
                __html: formatContentForPreview(form.content) || "Blog content will appear here..." 
              }} 
              style={{
                "& h2": { color: "#f7f5ec", fontSize: "1.5rem", marginTop: "32px", marginBottom: "16px" },
                "& h3": { color: "#f7f5ec", fontSize: "1.3rem", marginTop: "24px", marginBottom: "12px" },
                "& ul": { paddingLeft: "24px", marginBottom: "20px" },
                "& ol": { paddingLeft: "24px", marginBottom: "20px" },
                "& li": { marginBottom: "8px" },
                "& blockquote": { 
                  borderLeft: "4px solid #F7F5E6", 
                  paddingLeft: "20px", 
                  margin: "20px 0", 
                  fontStyle: "italic",
                  color: "#B8B7AA"
                },
                "& strong": { color: "#f7f5ec", fontWeight: "600" },
                "& em": { fontStyle: "italic", color: "#D6D0C3" },
                "& p": { marginBottom: "16px" }
              }}
            />
          </div>

          {/* Tags */}
          {form.tags && (
            <div style={{ marginTop: "40px" }}>
              <div style={{ 
                color: "#B8B7AA", 
                fontSize: "0.9rem", 
                marginBottom: "12px" 
              }}>
                Tags:
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {form.tags.split(',').map((tag, index) => (
                  <span key={index} style={{
                    background: "rgba(247,245,230,0.1)",
                    color: "#F7F5E6",
                    padding: "4px 12px",
                    borderRadius: "16px",
                    fontSize: "0.8rem",
                    border: "1px solid rgba(247,245,230,0.2)"
                  }}>
                    {tag.trim()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="add-blog-page" style={{ background: "#181816", color: "#FFFDEB", minHeight: "100vh", padding: "48px 0", fontFamily: 'Segoe UI, Arial, sans-serif' }}>
      <h1 style={{ textAlign: "center", fontSize: "2.5rem", marginBottom: 32, fontWeight: 700, letterSpacing: 1 }}>Add New Blog Post</h1>
      
      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: "fixed",
          top: 20,
          right: 20,
          background: toast.type === "success" ? "#4CAF50" : toast.type === "error" ? "#f44336" : "#2196F3",
          color: "white",
          padding: "16px 24px",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          zIndex: 1000,
          maxWidth: 400,
          fontSize: "0.95rem",
          fontWeight: 500
        }}>
          {toast.message}
        </div>
      )}
      
      {/* Authentication section */}
      {!user ? (
        <div style={{ maxWidth: 400, margin: "0 auto 48px", background: "rgba(28,28,28,0.98)", padding: 32, borderRadius: 12, border: "1px solid #35332c" }}>
          <h2 style={{ textAlign: "center", marginBottom: 24, color: "#f7f5ec" }}>Admin Login Required</h2>
          {!showLogin ? (
            <div style={{ textAlign: "center" }}>
              <p style={{ marginBottom: 20, color: "#D6D0C3" }}>You need to be logged in as an admin to publish blog posts.</p>
              <button 
                onClick={() => setShowLogin(true)}
                style={{ 
                  background: "#F7F5E6", 
                  color: "#181816", 
                  border: "none", 
                  borderRadius: 8, 
                  padding: "12px 24px", 
                  fontSize: "1rem", 
                  fontWeight: 600, 
                  cursor: "pointer" 
                }}
              >
                Login as Admin
              </button>
            </div>
          ) : (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8, color: "#f7f5ec" }}>Email</label>
                <input 
                  type="email" 
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  style={{ 
                    width: "100%", 
                    padding: 10, 
                    borderRadius: 6, 
                    border: "1px solid #35332c", 
                    background: "#23221e", 
                    color: "#FFFDEB" 
                  }} 
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8, color: "#f7f5ec" }}>Password</label>
                <input 
                  type="password" 
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  style={{ 
                    width: "100%", 
                    padding: 10, 
                    borderRadius: 6, 
                    border: "1px solid #35332c", 
                    background: "#23221e", 
                    color: "#FFFDEB" 
                  }} 
                />
              </div>
              {loginError && (
                <p style={{ color: "#ff6b6b", fontSize: "0.9rem", marginBottom: 16 }}>{loginError}</p>
              )}
              <button 
                type="submit"
                style={{ 
                  background: "#F7F5E6", 
                  color: "#181816", 
                  border: "none", 
                  borderRadius: 6, 
                  padding: "10px 20px", 
                  fontSize: "1rem", 
                  fontWeight: 600, 
                  cursor: "pointer",
                  width: "100%" 
                }}
              >
                Login
              </button>
            </form>
          )}
        </div>
      ) : (
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <p style={{ color: "#D6D0C3", marginBottom: 12 }}>
            Logged in as: <strong>{user.email}</strong>
          </p>
          <button 
            onClick={handleLogout}
            style={{ 
              background: "transparent", 
              color: "#f7f5ec", 
              border: "1px solid #35332c", 
              borderRadius: 6, 
              padding: "8px 16px", 
              fontSize: "0.9rem", 
              cursor: "pointer" 
            }}
          >
            Logout
          </button>
        </div>
      )}
      
      {/* Only show form controls and content if user is logged in */}
      {user && (
        <>
          {/* Toggle buttons */}
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <button 
              type="button"
              onClick={() => setActiveMode('edit')}
              style={{ 
                background: activeMode === 'edit' ? "#F7F5E6" : "transparent", 
                color: activeMode === 'edit' ? "#181816" : "#F7F5E6", 
                border: "2px solid #F7F5E6", 
                borderRadius: "8px 0 0 8px", 
                padding: "12px 24px", 
                fontSize: "1rem", 
                fontWeight: 600, 
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              Edit
            </button>
            <button 
              type="button"
              onClick={() => setActiveMode('preview')}
              style={{ 
                background: activeMode === 'preview' ? "#F7F5E6" : "transparent", 
                color: activeMode === 'preview' ? "#181816" : "#F7F5E6", 
                border: "2px solid #F7F5E6", 
                borderLeft: "1px solid #F7F5E6",
                borderRadius: "0", 
                padding: "12px 24px", 
                fontSize: "1rem", 
                fontWeight: 600, 
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              Preview
            </button>
            <button 
              type="button"
              onClick={() => setActiveMode('manage')}
              style={{ 
                background: activeMode === 'manage' ? "#F7F5E6" : "transparent", 
                color: activeMode === 'manage' ? "#181816" : "#F7F5E6", 
                border: "2px solid #F7F5E6", 
                borderLeft: "1px solid #F7F5E6",
                borderRadius: "0 8px 8px 0", 
                padding: "12px 24px", 
                fontSize: "1rem", 
                fontWeight: 600, 
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              Manage
            </button>
          </div>

          {activeMode === 'Manage' ? (
            <div>
              {renderPreview()}
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <button 
                  type="button"
                  onClick={() => setActiveMode('edit')}
                  style={{ 
                    background: "#F7F5E6", 
                    color: "#181816", 
                    border: "none", 
                    borderRadius: 8, 
                    padding: "16px 32px", 
                    fontSize: "1.12rem", 
                    fontWeight: 600, 
                    cursor: "pointer", 
                    marginRight: 16,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.10)"
                  }}
                >
                  Back to Edit
                </button>
                <button 
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  style={{ 
                    background: isSubmitting ? "#ccc" : "linear-gradient(90deg,#F7F5E6 60%,#e7e3c7 100%)", 
                    color: "#181816", 
                    border: "none", 
                    borderRadius: 8, 
                    padding: "16px 32px", 
                    fontSize: "1.12rem", 
                    fontWeight: 600, 
                    cursor: isSubmitting ? "not-allowed" : "pointer", 
                    boxShadow: "0 2px 12px rgba(0,0,0,0.10)"
                  }}
                >
                  {isSubmitting ? "Publishing..." : "Publish Blog"}
                </button>
              </div>
            </div>
          ) : activeMode === 'manage' ? (
            <div style={{ maxWidth: 800, margin: "0 auto", background: "rgba(28,28,28,0.98)", padding: 40, borderRadius: 18, boxShadow: "0 8px 40px rgba(0,0,0,0.22)", border: "1px solid #23221e" }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ fontSize: "1.18rem", fontWeight: 600, color: "#F7F5E6" }}>Manage Blogs</h2>
                <button
                  onClick={() => setActiveMode('edit')}
                  style={{ 
                    background: "#F7F5E6",
                    color: "#181816",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 16px",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    cursor: "pointer"
                  }}
                >
                  Add New Blog
                </button>
              </div>
              <div style={{ marginBottom: 20 }}>
                {isLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#B8B7AA' }}>
                    Loading blogs...
                  </div>
                ) : !blogs.length ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#B8B7AA' }}>
                    No blogs found. Create your first blog!
                  </div>
                ) : (
                  blogs.map(blog => (
                    <div 
                    key={blog.id}
                    style={{ 
                      padding: 24,
                      borderRadius: 12,
                      background: 'rgba(35,34,30,0.5)',
                      marginBottom: 16,
                      border: '1px solid #35332c'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                      <div>
                        <div style={{ color: '#B8B7AA', fontSize: '0.9rem', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '1px' }}>
                          [{blog.category}]
                        </div>
                        <h3 style={{ fontSize: '1.5rem', color: '#F7F5E6', marginBottom: 12, fontWeight: 600 }}>
                          {blog.title}
                        </h3>
                        <p style={{ color: '#D6D0C3', fontSize: '1rem', marginBottom: 16 }}>
                          {blog.summary}
                        </p>
                        <div style={{ display: 'flex', gap: 16, color: '#B8B7AA', fontSize: '0.9rem' }}>
                          <span>By {blog.author}</span>
                          <span>•</span>
                          <span>{blog.readTime}</span>
                          <span>•</span>
                          <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => handleEditBlog(blog)}
                          style={{ 
                            background: 'transparent',
                            color: '#F7F5E6',
                            border: '1px solid #35332c',
                            borderRadius: 6,
                            padding: '8px 16px',
                            fontSize: '0.9rem',
                            cursor: 'pointer'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteBlog(blog.id)}
                          style={{ 
                            background: 'rgba(244,67,54,0.1)',
                            color: '#f44336',
                            border: '1px solid rgba(244,67,54,0.3)',
                            borderRadius: 6,
                            padding: '8px 16px',
                            fontSize: '0.9rem',
                            cursor: 'pointer'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <form className="add-blog-form" onSubmit={handleSubmit} style={{ maxWidth: 800, margin: "0 auto", background: "rgba(28,28,28,0.98)", padding: 40, borderRadius: 18, boxShadow: "0 8px 40px rgba(0,0,0,0.22)", border: "1px solid #23221e" }}>
              
              {/* Basic Information */}
              <div style={{ marginBottom: 28 }}>
                <h2 style={{ fontSize: "1.18rem", fontWeight: 600, marginBottom: 18, color: "#F7F5E6" }}>Basic Information</h2>
                <label style={{ display: "block", marginBottom: 14 }}>
                  Blog Title *
                  <input 
                    type="text" 
                    name="title" 
                    value={form.title} 
                    onChange={handleChange} 
                    placeholder="Enter an engaging blog title..."
                    required
                    style={{ width: "100%", padding: 12, marginTop: 6, borderRadius: 8, border: "1px solid #35332c", background: "#23221e", color: "#FFFDEB", fontSize: "1rem" }} 
                  />
                </label>
                <label style={{ display: "block", marginBottom: 14 }}>
                  Summary *
                  <textarea 
                    name="summary" 
                    value={form.summary} 
                    onChange={handleChange} 
                    placeholder="Write a compelling summary that will appear in blog listings..."
                    rows={3}
                    required
                    style={{ 
                      width: "100%", 
                      padding: 12, 
                      marginTop: 6, 
                      borderRadius: 8, 
                      border: "1px solid #35332c", 
                      background: "#23221e", 
                      color: "#FFFDEB", 
                      fontSize: "1rem",
                      resize: "vertical",
                      fontFamily: "inherit"
                    }} 
                  />
                </label>
                <label style={{ display: "block", marginBottom: 14 }}>
                  Category
                  <select 
                    name="category" 
                    value={form.category} 
                    onChange={handleChange} 
                    style={{ width: "100%", padding: 12, marginTop: 6, borderRadius: 8, border: "1px solid #35332c", background: "#23221e", color: "#FFFDEB", fontSize: "1rem" }}
                  >
                    <option value="Design Philosophy">Design Philosophy</option>
                    <option value="Sustainability">Sustainability</option>
                    <option value="Color Theory">Color Theory</option>
                    <option value="Smart Home">Smart Home</option>
                    <option value="Case Studies">Case Studies</option>
                    <option value="Trends">Trends</option>
                    <option value="Tips & Guides">Tips & Guides</option>
                  </select>
                </label>
              </div>

              {/* Content */}
              <div style={{ marginBottom: 28 }}>
                <h2 style={{ fontSize: "1.18rem", fontWeight: 600, marginBottom: 18, color: "#F7F5E6" }}>Content</h2>
                <label style={{ display: "block", marginBottom: 14 }}>
                  Blog Content *
                  <textarea 
                    name="content" 
                    value={form.content} 
                    onChange={handleChange} 
                    placeholder={`Write your blog content here. Example:

## Introduction
Welcome to our design philosophy guide. This post will explore modern interior design principles.

### Key Design Elements
Here are the essential elements we focus on:

- **Natural lighting** for ambient atmosphere
- **Sustainable materials** for eco-friendly living
- **Minimalist aesthetics** for clean spaces
- **Functional layouts** for daily convenience

### Step-by-Step Process
Our design process follows these steps:

1. Initial consultation and space assessment
2. Concept development and mood boards
3. Material selection and sourcing
4. Implementation and project management
5. Final styling and completion

> "Good design is about creating spaces that enhance daily life while reflecting personal style."

**Important considerations:**
- Budget planning is *essential* for success
- Timeline management keeps projects on track
- Regular communication ensures client satisfaction

### Conclusion
Modern interior design combines functionality with aesthetic appeal...

You can also use HTML tags for advanced formatting.`}
                    rows={15}
                    required
                    style={{ 
                      width: "100%", 
                      padding: 12, 
                      marginTop: 6, 
                      borderRadius: 8, 
                      border: "1px solid #35332c", 
                      background: "#23221e", 
                      color: "#FFFDEB", 
                      fontSize: "1rem",
                      resize: "vertical",
                      fontFamily: "monospace",
                      lineHeight: "1.5"
                    }} 
                  />
                </label>
                  <div style={{ 
                  fontSize: "0.85rem", 
                  color: "#B8B7AA", 
                  marginTop: 8,
                  background: "rgba(247,245,230,0.05)",
                  padding: "12px",
                  borderRadius: "6px",
                  border: "1px solid rgba(247,245,230,0.1)"
                }}>
                  <div style={{ fontWeight: "600", marginBottom: "8px", color: "#F7F5E6" }}>Formatting Guide:</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                    <div>
                      <div><strong>Headers:</strong> ## Main, ### Sub</div>
                      <div><strong>Bold:</strong> **text** or __text__</div>
                      <div><strong>Italic:</strong> *text* or _text_</div>
                    </div>
                    <div>
                      <div><strong>Bullets:</strong> - item</div>
                      <div><strong>Numbers:</strong> 1. item</div>
                      <div><strong>Quote:</strong> {'>'} text</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div style={{ marginBottom: 28 }}>
                <h2 style={{ fontSize: "1.18rem", fontWeight: 600, marginBottom: 18, color: "#F7F5E6" }}>Metadata</h2>
                <label style={{ display: "block", marginBottom: 14 }}>
                  Author
                  <input 
                    type="text" 
                    name="author" 
                    value={form.author} 
                    onChange={handleChange} 
                    style={{ width: "100%", padding: 12, marginTop: 6, borderRadius: 8, border: "1px solid #35332c", background: "#23221e", color: "#FFFDEB", fontSize: "1rem" }} 
                  />
                </label>
                <label style={{ display: "block", marginBottom: 14 }}>
                  Featured Image URL
                  <input 
                    type="url" 
                    name="image" 
                    value={form.image} 
                    onChange={handleChange} 
                    placeholder="https://example.com/image.jpg"
                    style={{ width: "100%", padding: 12, marginTop: 6, borderRadius: 8, border: "1px solid #35332c", background: "#23221e", color: "#FFFDEB", fontSize: "1rem" }} 
                  />
                </label>
                <label style={{ display: "block", marginBottom: 14 }}>
                  Tags (comma-separated)
                  <input 
                    type="text" 
                    name="tags" 
                    value={form.tags} 
                    onChange={handleChange} 
                    placeholder="interior design, minimalism, sustainable living"
                    style={{ width: "100%", padding: 12, marginTop: 6, borderRadius: 8, border: "1px solid #35332c", background: "#23221e", color: "#FFFDEB", fontSize: "1rem" }} 
                  />
                </label>
                <label style={{ display: "block", marginBottom: 14 }}>
                  Read Time
                  <input 
                    type="text" 
                    name="readTime" 
                    value={form.readTime} 
                    onChange={handleChange} 
                    placeholder="5 min read"
                    style={{ width: "100%", padding: 12, marginTop: 6, borderRadius: 8, border: "1px solid #35332c", background: "#23221e", color: "#FFFDEB", fontSize: "1rem" }} 
                  />
                </label>
                <label style={{ display: "block", marginBottom: 14 }}>
                  Status
                  <select 
                    name="status" 
                    value={form.status} 
                    onChange={handleChange} 
                    style={{ width: "100%", padding: 12, marginTop: 6, borderRadius: 8, border: "1px solid #35332c", background: "#23221e", color: "#FFFDEB", fontSize: "1rem" }}
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </label>
              </div>

              <button 
                type="submit" 
                disabled={isSubmitting} 
                style={{ 
                  background: isSubmitting ? "#ccc" : "linear-gradient(90deg,#F7F5E6 60%,#e7e3c7 100%)", 
                  color: "#181816", 
                  border: "none", 
                  borderRadius: 8, 
                  padding: "16px 0", 
                  fontSize: "1.12rem", 
                  fontWeight: 600, 
                  cursor: isSubmitting ? "not-allowed" : "pointer", 
                  marginTop: 10, 
                  boxShadow: "0 2px 12px rgba(0,0,0,0.10)", 
                  width: "100%", 
                  letterSpacing: 0.5, 
                  transition: "background 0.2s" 
                }}
              >
                {isSubmitting ? "Publishing..." : "Publish Blog Post"}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}